/**
 * PSR Phase 2 — normalized sentiment facts → CitationRef for G1 verifier.
 */
import type {
  CitationRef,
  SentimentFetchResult,
  SentimentNormalizedFact,
} from '../../services/sentimentFetchers/types';

const FALLBACK_URL = 'https://data.deepatomic.internal/sentiment-fact';

export function factToCitationRef(
  fact: SentimentNormalizedFact,
  sourceId: CitationRef['sourceId'],
  index: number,
): CitationRef {
  const label = `${fact.label}: ${fact.value}${fact.units ? ` ${fact.units}` : ''}`;
  return {
    index,
    label: label.slice(0, 240),
    url: (fact.url ?? FALLBACK_URL).trim(),
    sourceId,
    factKey: fact.key,
  };
}

/** Flatten tier-1 fetch results into contiguous citation refs (1-based indices). */
export function buildCitationRefsFromFetchResults(
  results: readonly SentimentFetchResult[],
  startIndex = 1,
): CitationRef[] {
  const out: CitationRef[] = [];
  let idx = startIndex;
  for (const r of results) {
    for (const fact of r.normalizedFacts) {
      out.push(factToCitationRef(fact, r.sourceId, idx));
      idx += 1;
    }
  }
  return out;
}

/** Provenance strings for citationIntegrityVerifier tier-1 cache extension. */
export function sentimentFactsToProvenanceStrings(
  results: readonly SentimentFetchResult[],
): { sources: Set<string>; urls: Set<string> } {
  const sources = new Set<string>();
  const urls = new Set<string>();
  for (const r of results) {
    sources.add(r.sourceId);
    for (const fact of r.normalizedFacts) {
      sources.add(`${r.sourceId}:${fact.key}`);
      if (fact.url) urls.add(fact.url.trim().toLowerCase());
    }
    for (const ref of r.citationRefs) {
      if (ref.url) urls.add(ref.url.trim().toLowerCase());
    }
  }
  return { sources, urls };
}
